'use strict';

import { FullScreenQuad, Pass } from 'three/examples/jsm/postprocessing/Pass';
import {
	Color, FloatType,
	MeshBasicMaterial, NearestFilter, RedFormat,
	ShaderMaterial,
	UniformsUtils,
	WebGLRenderTarget,
} from 'three';
import vertex from './Shaders/DynamicExposure/vertex.glsl';
import fragment from './Shaders/DynamicExposure/fragment.glsl';
import final_vertex from './Shaders/DynamicExposure/final/vertex.glsl';
import final_fragment from './Shaders/DynamicExposure/final/fragment.glsl';
import { Effect } from './Effect';
import { DownsampleShader } from './Shaders/DownsampleShader/DownsampleShader';
import { LuminanceShader } from './Shaders/LuminanceShader/LuminanceShader';

class DynamicExposurePass extends Pass{
	constructor (renderer,width,height) {
		super();
		
		this.width = width;
		this.height = height;
		this.renderer = renderer;
		
		this.samples = 2;
		this.downsample = {
			materials: [],
			renderTargets: []
		};
		
		this.createDownsampleMaterials();
		
		this.averageLuminanceRT = new WebGLRenderTarget(1,1,{
			minFilter: NearestFilter,
			magFilter: NearestFilter,
			format: RedFormat,
			type: FloatType
		});
		
		this.luminanceRT_A = new WebGLRenderTarget(1,1,{
			minFilter: NearestFilter,
			magFilter: NearestFilter,
			format: RedFormat,
			type: FloatType
		});
		
		this.luminanceRT_B = new WebGLRenderTarget(1,1,{
			minFilter: NearestFilter,
			magFilter: NearestFilter,
			format: RedFormat,
			type: FloatType
		});
		
		this.activeExposureRT = this.luminanceRT_A;
		this.previousExposureRT = this.luminanceRT_B;
		
		this.luminanceMaterial = new ShaderMaterial({
			uniforms: UniformsUtils.clone(LuminanceShader.uniforms),
			vertexShader: LuminanceShader.vertexShader,
			fragmentShader: LuminanceShader.fragmentShader,
			transparent: true,
			depthWrite: false,
			depthTest: false
		});
		
		this.luminanceMixMaterial = new ShaderMaterial({
			uniforms: {
				tPrevLuminance: {value: null},
				tAvgLuminance: {value: null},
				targetLuminance: {value: 0.5},
				exposureSpeed: {value: 2},
				minExposure: {value: 1},
				maxExposure: {value: 1.35},
				deltaTime: {value: 0}
			},
			vertexShader: vertex,
			fragmentShader: fragment,
			transparent: true,
			depthWrite: false,
			depthTest: false
		});
		
		this.exposureMaterial = new ShaderMaterial({
			uniforms: {
				tDiffuse: {value: null},
				currentExposureTexture: {value: null}
			},
			vertexShader: final_vertex,
			fragmentShader: final_fragment,
			transparent: true,
			depthWrite: false,
			depthTest: false
		});
		
		this.fsQuad = new FullScreenQuad(null);
		
		setTimeout(() => {
			const initMaterial = new MeshBasicMaterial({color: 0xff0000});
			this.renderPass(this.renderer,initMaterial,this.previousExposureRT);
			this.renderer.setRenderTarget(null);
		},0);
		
		this.originalClearColor = new Color();
		this.needsSwap = false;
	}
	
	createDownsampleMaterials() {
		
		while (this.downsample.materials.length) {
			const material = this.downsample.materials.shift();
			material.dispose();
		}
		
		while (this.downsample.renderTargets.length) {
			const rt = this.downsample.renderTargets.shift();
			rt.dispose();
		}
		
		let width = this.width;
		let height = this.height;
		
		while (width > 1 || height > 1) {
			width = Math.max(1, Math.floor(width / this.samples));
			height = Math.max(1, Math.floor(height / this.samples));
			
			const rt = new WebGLRenderTarget(width,height);
			
			const material = new ShaderMaterial({
				uniforms: UniformsUtils.clone(DownsampleShader.uniforms),
				vertexShader: DownsampleShader.vertexShader,
				fragmentShader: DownsampleShader.fragmentShader,
				transparent: true,
				depthWrite: false,
				depthTest: false
			});
			
			material.uniforms.texelSize.value.set(1 / width, 1/ height);
			this.downsample.materials.push(material);
			this.downsample.renderTargets.push(rt);
		}
	}
	
	tick(delta) {
		this.luminanceMixMaterial.uniforms.deltaTime.value = delta;
	}
	
	render (renderer, writeBuffer, readBuffer) {
		let texture = readBuffer.texture;
		
		// Downsample
		for (let i = 0; i < this.downsample.renderTargets.length; i ++) {
			const rt = this.downsample.renderTargets[i];
			const material = this.downsample.materials[i];
			material.uniforms.tDiffuse.value = texture;
			this.renderPass(renderer,material,rt);
			texture = rt.texture;
		}
		
		// Get luminance
		this.luminanceMaterial.uniforms.tDiffuse.value = texture;
		this.renderPass(renderer,this.luminanceMaterial,this.averageLuminanceRT);
		
		// Mix luminance
		this.luminanceMixMaterial.uniforms.tAvgLuminance.value = this.averageLuminanceRT.texture;
		this.luminanceMixMaterial.uniforms.tPrevLuminance.value = this.previousExposureRT.texture;
		this.renderPass(renderer,this.luminanceMixMaterial,this.activeExposureRT);
		
		//Apply luminance
		this.exposureMaterial.uniforms.tDiffuse.value = readBuffer.texture;
		this.exposureMaterial.uniforms.currentExposureTexture.value = this.activeExposureRT.texture;
		
		this.renderPass(renderer,this.exposureMaterial,this.renderToScreen ? null : readBuffer);
		
		// Swap luminance render targets
		[this.activeExposureRT, this.previousExposureRT] = [this.previousExposureRT, this.activeExposureRT];
	}
	
	setSize(width,height) {
		this.width = width;
		this.height = height;
		this.createDownsampleMaterials();
	}
	
	renderPass( renderer, passMaterial, renderTarget, clearColor, clearAlpha ) {
		// save original state
		renderer.getClearColor( this.originalClearColor );
		const originalClearAlpha = renderer.getClearAlpha();
		const originalAutoClear = renderer.autoClear;
		
		renderer.setRenderTarget( renderTarget );
		
		// setup pass state
		renderer.autoClear = false;
		if ( ( clearColor !== undefined ) && ( clearColor !== null ) ) {
			
			renderer.setClearColor( clearColor );
			renderer.setClearAlpha( clearAlpha || 0.0 );
			renderer.clear();
			
		}
		
		this.fsQuad.material = passMaterial;
		this.fsQuad.render( renderer );
		
		// restore original state
		renderer.autoClear = originalAutoClear;
		renderer.setClearColor( this.originalClearColor );
		renderer.setClearAlpha( originalClearAlpha );
	}
	
}


export class DynamicExposureEffect extends Effect{
	constructor (renderer,width,height) {
		super();
		
		this.effect = new DynamicExposurePass(renderer,width,height);
		this.active = false;
	}
	
	update(state) {
		if (this.active) {
			this.effect.update(state)
		}
	}
	
	tick(delta) {
		if (this.active) {
			this.effect.tick(delta);
		}
	}
	
	get config() {
		return {
			active: this.active,
			targetLuminance: this.effect.luminanceMixMaterial.uniforms.targetLuminance.value,
			exposureSpeed: this.effect.luminanceMixMaterial.uniforms.exposureSpeed.value,
			minExposure: this.effect.luminanceMixMaterial.uniforms.minExposure.value,
			maxExposure: this.effect.luminanceMixMaterial.uniforms.maxExposure.value
		};
	}
	
	set config(config) {
		Object.entries(config).forEach(([key,value]) => {
			if (key === 'active') {
				this.active = value;
			} else {
				this.effect.luminanceMixMaterial.uniforms[key].value = value;
			}
		});
	}
}
